import React, { useEffect } from 'react';
import { motion, useAnimation, useMotionValue } from 'framer-motion';

interface SimpleCircularTextProps {
  text: string;
  spinDuration?: number;
  onHover?: 'speedUp' | 'slowDown' | 'pause' | 'goBonkers';
  className?: string;
}

const getRotationTransition = (duration: number, from: number, loop = true) => ({
  from,
  to: from + 360,
  ease: 'linear' as const,
  duration,
  type: 'tween' as const,
  repeat: loop ? Infinity : 0,
});

const getTransition = (duration: number, from: number) => ({
  rotate: getRotationTransition(duration, from),
  scale: {
    type: 'spring' as const,
    damping: 20,
    stiffness: 300,
  },
});

const SimpleCircularText: React.FC<SimpleCircularTextProps> = ({ 
  text, 
  spinDuration = 20,
  onHover = 'speedUp',
  className = ''
}) => {
  const letters = Array.from(text);
  const controls = useAnimation();
  const rotation = useMotionValue(0);

  useEffect(() => {
    const start = rotation.get();
    controls.start({
      rotate: start + 360,
      scale: 1,
      transition: getTransition(spinDuration, start) as any,
    });
  }, [spinDuration, text, onHover, controls, rotation]);

  const handleHoverStart = () => {
    const start = rotation.get();
    if (!onHover) return;

    let transitionConfig;
    let scaleVal = 1;

    switch (onHover) {
      case 'slowDown':
        transitionConfig = getTransition(spinDuration * 2, start);
        break;
      case 'speedUp':
        transitionConfig = getTransition(spinDuration / 4, start);
        break;
      case 'pause':
        transitionConfig = {
          rotate: { type: 'spring' as const, damping: 20, stiffness: 300 },
          scale: { type: 'spring' as const, damping: 20, stiffness: 300 },
        };
        scaleVal = 1;
        break;
      case 'goBonkers':
        transitionConfig = getTransition(spinDuration / 20, start);
        scaleVal = 0.8;
        break;
      default:
        transitionConfig = getTransition(spinDuration, start);
    }

    controls.start({
      rotate: start + 360,
      scale: scaleVal,
      transition: transitionConfig as any,
    });
  };

  const handleHoverEnd = () => {
    const start = rotation.get();
    controls.start({
      rotate: start + 360,
      scale: 1,
      transition: getTransition(spinDuration, start) as any,
    });
  };

  const circularTextStyle = {
    margin: '0 auto',
    borderRadius: '50%',
    width: '200px',
    position: 'relative' as const,
    height: '200px',
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'center' as const,
    cursor: 'pointer',
    transformOrigin: '50% 50%',
    WebkitTransformOrigin: '50% 50%',
  };

  return (
    <motion.div
      className={`circular-text ${className}`}
      style={{ ...circularTextStyle, rotate: rotation }}
      initial={{ rotate: 0 }}
      animate={controls}
      onMouseEnter={handleHoverStart}
      onMouseLeave={handleHoverEnd}
    >
      {letters.map((letter, i) => {
        const rotationDeg = (360 / letters.length) * i;
        const factor = Math.PI / letters.length;
        const x = factor * i;
        const y = factor * i;
        const transform = `rotateZ(${rotationDeg}deg) translate3d(${x}px, ${y}px, 0)`;

        return (
          <span 
            key={i} 
            style={{ 
              transform, 
              WebkitTransform: transform,
              position: 'absolute',
              display: 'inline-block',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              fontSize: 'clamp(20px, 3vw, 28px)',
              fontWeight: 900,
              transition: 'all 0.5s cubic-bezier(0, 0, 0, 1)',
            }}
          >
            {letter}
          </span>
        );
      })}
    </motion.div>
  );
};

export default SimpleCircularText;